import { find } from "lodash";
import template from "./coordinate.html";
import "./coordinate.less";

import deg from "./modes/decimal-degrees.js";
import ddm from "./modes/degrees-decimal-minutes.js";
import {
	EVENT_CLEAR_VALUE,
	EVENT_FIELD_DISABLE,
	EVENT_FIELD_ENABLE,
	EVENT_REPORT_LOCKED,
	EVENT_REVERT_VALUES,
	EVENT_SET_FORM_PRISTINE,
	EVENT_SET_VALUE,
	EVENT_UNCLEAR_VALUE,
	EVENT_VALUE_CHANGED,
	FEATURE_TYPES,
	FIELD_TEMPLATES
} from "constants.js";
import { roundToRoundingPoint } from "services/math-service.js";
import {
	COORDINATE_MODE_CODES,
	getNextCoordinateMode,
	resolveCoordinateModes
} from "./coordinate-modes.ts";
import { getDecomposedDdm } from "services/geo-service.ts";
import { isValueNullish } from "../../../../utils/valueUtilities";
import { CoordinateFieldAdapter } from "../../../../services/activity-trigger";

function hasCoordinatePart(value) {
	return value !== undefined && value !== null && value !== "";
}

function cloneCoordinate(value, fieldId) {
	if(!value) {
		return undefined;
	}

	return {
		FieldId: value.FieldId ?? fieldId,
		Latitude: value.Latitude,
		Longitude: value.Longitude
	};
}

export class Controller {
	lookups = [];

	constructor($scope, $logger) {
		this._scope = $scope;
		this.$logger = $logger;
	}

	$onInit() {
		this.capabilities = {
			activityTrigger: new CoordinateFieldAdapter(
				this.field,
				() => this.editorHasInput,
				value => this.setValue(value, true)
			)
		};
		this._eventBus = this.reportScope.events;
		this._unregister = this.reportFormCtrl.registerFieldController(this);

		const resolvedModes = resolveCoordinateModes(this.field);
		this.mode = resolvedModes.activeMode;
		this.availableModes = resolvedModes.availableModes;
		this.canSwitchModes = resolvedModes.canSwitch;

		const sourceValue = find(this.values, value => value.FieldId === this.field.Id);
		this.initialValue = cloneCoordinate(sourceValue, this.field.Id);
		this.setValue(sourceValue);
		this.setFeatures();
		this.registerEvents();
		this._initialized = true;

		if(resolvedModes.isUnsupportedFixedMode) {
			this.$logger?.warn(`Unsupported coordinate mode "${this.field.Mode}"; using Decimal Degrees.`);
		}
	}

	$onChanges(changes) {
		if(!this._initialized || isValueNullish(changes.values) || changes.values.currentValue === changes.values.previousValue) {
			return;
		}

		const sourceValue = find(changes.values.currentValue, value => value.FieldId === this.field.Id);
		this.initialValue = cloneCoordinate(sourceValue, this.field.Id);
		this.setValue(sourceValue);
	}

	$onDestroy() {
		this._unregister();
	}

	registerEvents() {
		this._eventBus.register(EVENT_SET_VALUE(this.field.Id), (event, params) => {
			this.setValue(params?.value, true);
		}, this._scope);

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), (event, params = {}) => {
			if(!this.cleared && this.editorHasInput && params.isInitializing !== true && params.doNotSave !== true) {
				this.cleared = {
					value: cloneCoordinate(this.currentValue, this.field.Id),
					hasInput: this.editorHasInput
				};
			}

			this.setValue(undefined, params.isInitializing !== true);
		}, this._scope);

		this._eventBus.register(EVENT_UNCLEAR_VALUE(this.field.Id), () => {
			if(!this.cleared) {
				return;
			}

			const cleared = this.cleared;
			this.cleared = undefined;
			this.setValue(cleared.value, true, cleared.hasInput);
		}, this._scope);

		this._eventBus.register(EVENT_FIELD_DISABLE(this.field.Id), () => this.disableField(), this._scope);
		this._eventBus.register(EVENT_FIELD_ENABLE(this.field.Id), () => this.enableField(), this._scope);
		this._eventBus.register(EVENT_REPORT_LOCKED, () => this.field.isReadonly = true, this._scope);
		this._eventBus.register(EVENT_REVERT_VALUES, () => this.revertValue(), this._scope);
		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => this.setPristine(), this._scope);
	}

	rotateMode() {
		if(!this.canSwitchModes) {
			return;
		}

		this.editorValue = cloneCoordinate(this.currentValue, this.field.Id);
		this.mode = getNextCoordinateMode(this.mode, this.availableModes);
		this.lookups.forEach(lookup => lookup.mode = this.mode);
	}

	getModeSwitchTooltip() {
		const nextMode = getNextCoordinateMode(this.mode, this.availableModes);
		const mode = this.availableModes.find(availableMode => availableMode.code === nextMode);

		return mode ? `Switch to ${mode.label}` : "Switch coordinate format";
	}

	handleEditorValueChanged(value, hasInput, triggerEvent) {
		this.currentValue = cloneCoordinate(value, this.field.Id);
		this.editorHasInput = hasInput === true;
		this.updateDirtyState();

		if(triggerEvent === true) {
			this.triggerValueChanged();
		}
	}

	setValue(value, notify = false, hasInput = undefined) {
		this.currentValue = cloneCoordinate(value, this.field.Id);
		this.editorValue = cloneCoordinate(value, this.field.Id);
		this.editorHasInput = hasInput ?? this.hasCoordinateInput(value);
		this.updateDirtyState();

		if(notify) {
			this.triggerValueChanged();
		}
	}

	getPersistedValue() {
		if(!hasCoordinatePart(this.currentValue?.Latitude)
			|| !hasCoordinatePart(this.currentValue?.Longitude)) {
			return undefined;
		}

		return cloneCoordinate(this.currentValue, this.field.Id);
	}

	getCurrentValue = (isApiCall) => {
		let result = this.getPersistedValue();

		if(!isApiCall) {
			if(!result) {
				result = {
					FieldId: this.field.Id,
					hasResultForRuleRun: false
				};
			}

			result.factor = 1;
			result.isDirty = this.isDirty(result);
		}

		return result ? [result] : [];
	}

	isDirty(result = this.getPersistedValue()) {
		const hasChangedValue = result?.Latitude != this.initialValue?.Latitude
			|| result?.Longitude != this.initialValue?.Longitude;
		const hasIncompleteInput = this.editorHasInput && !this.getPersistedValue();

		return (this.field.visible !== false || this.initialValue !== undefined)
			&& this.field.IsNotReported !== true
			&& this.field.IsHidden !== true
			&& (hasChangedValue || hasIncompleteInput);
	}

	updateDirtyState() {
		this.setFieldDirty(this.field.Id, this.isDirty());
	}

	revertValue() {
		this.setValue(this.initialValue, true);
	}

	setPristine() {
		const pristineValue = this.field.IsDetail ? undefined : this.getPersistedValue();
		this.initialValue = cloneCoordinate(pristineValue, this.field.Id);
		this.currentValue = cloneCoordinate(pristineValue, this.field.Id);
		this.editorValue = cloneCoordinate(pristineValue, this.field.Id);
		this.editorHasInput = this.hasCoordinateInput(pristineValue);
		this.updateDirtyState();
	}

	hasCoordinateInput(value) {
		return hasCoordinatePart(value?.Latitude) || hasCoordinatePart(value?.Longitude);
	}

	triggerValueChanged() {
		this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id), {
			field: this.field
		});
	}

	enableField() {
		this.field.isDisabled = false;
		this.field.placeHolder = null;
		this.lookups.forEach(lookup => lookup.disabled = false);
	}

	disableField() {
		this.field.isDisabled = true;
		// Preserve the existing coordinate-map behavior while the field is disabled.
		this.lookups.forEach(lookup => lookup.disabled = false);
	}

	setFeatures() {
		const mapFeature = find(this.field.features, { type: FEATURE_TYPES.map });
		const relatedFields = (mapFeature?.data ?? []).reduce((fields, relatedField) => {
			if(relatedField.Id !== this.field.Id && relatedField.Template === FIELD_TEMPLATES.Selection) {
				fields.push(relatedField);
			}

			return fields;
		}, []);

		this.lookups.push({
			name: FEATURE_TYPES.boundlessMapPoint,
			roundingPoint: this.field.RoundingPoint,
			fieldId: this.field.Id,
			fieldName: this.field.Name,
			disabled: false,
			relatedFields,
			defaultPosition: (
				this.field.Center && this.field.Zoom ? {
					center: this.field.Center.split(",").map(value => parseFloat(value)),
					zoom: parseFloat(this.field.Zoom)
				} : undefined
			),
			mode: this.mode,
			getCurrentValue: () => this.getCurrentValue(true),
			isUpdatable: () => !this.field.isReadonly && !this.field.isDisabled,
			degreeMapper: rawDegrees => this.mode === COORDINATE_MODE_CODES.decimalDegrees
				? roundToRoundingPoint(rawDegrees, this.field)
				: getDecomposedDdm(rawDegrees),
			action: selection => this.setValue({
				FieldId: this.field.Id,
				Latitude: roundToRoundingPoint(selection.lat, this.field),
				Longitude: roundToRoundingPoint(selection.lng, this.field)
			}, true)
		});
	}

	handleLookupSelection(lookup) {
		if(lookup.action) {
			lookup.action(lookup.selection);
		}
	}
}

export default angular
	.module("vesl.components.report-form.coordinate", [deg, ddm])
	.component("coordinate", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;
